import axios from "axios";
import moment from "moment";
import { addLog } from "./logger";
import { API_ROOT } from "../constants";
import { errorResponse, tempId } from "../helpers";
import { MAX_CONCURRENT_BOOKINGS, MAX_PEOPLE_PER_SESSION } from "../constants";
import { LOGIN_REQUEST_FAIL } from "./session";

export const GET_CUSTOMER_ORDER_START = "GET_CUSTOMER_ORDER_START";
export const GET_CUSTOMER_ORDER_SUCCESS = "GET_CUSTOMER_ORDER_SUCCESS";
export const GET_CUSTOMER_ORDER_FAIL = "GET_CUSTOMER_ORDER_FAIL";

export const ADD_CUSTOMER_BOOKING_START = "ADD_CUSTOMER_BOOKING_START";
export const ADD_CUSTOMER_BOOKING_SUCCESS = "ADD_CUSTOMER_BOOKING_SUCCESS";
export const ADD_CUSTOMER_BOOKING_FAIL = "ADD_CUSTOMER_BOOKING_FAIL";

export const REMOVE_CUSTOMER_BOOKING_START = "REMOVE_CUSTOMER_BOOKING_START";
export const REMOVE_CUSTOMER_BOOKING_SUCCESS = "REMOVE_CUSTOMER_BOOKING_SUCCESS";
export const REMOVE_CUSTOMER_BOOKING_FAIL = "REMOVE_CUSTOMER_BOOKING_FAIL";

export const UPDATE_CUSTOMER_BOOKING_START = "UPDATE_CUSTOMER_BOOKING_START";
export const UPDATE_CUSTOMER_BOOKING_SUCCESS = "UPDATE_CUSTOMER_BOOKING_SUCCESS";
export const UPDATE_CUSTOMER_BOOKING_FAIL = "UPDATE_CUSTOMER_BOOKING_FAIL";

export const SET_CUSTOMER_BOOKING_PEOPLE = "SET_CUSTOMER_BOOKING_PEOPLE";
export const ADD_CUSTOMER_BOOKING_EXTRA = "SET_CUSTOMER_BOOKING_EXTRA";
export const REMOVE_CUSTOMER_BOOKING_EXTRA = "REMOVE_CUSTOMER_BOOKING_EXTRA";

export const CHECKOUT_START = "CHECKOUT_START";
export const CHECKOUT_SUCCESS = "CHECKOUT_SUCCESS";
export const CHECKOUT_FAIL = "CHECKOUT_FAIL";

export const LOYALTY_CHECKOUT_START = "LOYALTY_CHECKOUT_START";
export const LOYALTY_CHECKOUT_SUCCESS = "LOYALTY_CHECKOUT_SUCCESS";
export const LOYALTY_CHECKOUT_FAIL = "LOYALTY_CHECKOUT_FAIL";

export const PING_CUSTOMER_BOOKING_START = "PING_CUSTOMER_BOOKING_START";
export const PING_CUSTOMER_BOOKING_SUCCESS = "PING_CUSTOMER_BOOKING_SUCCESS";
export const PING_CUSTOMER_BOOKING_FAIL = "PING_CUSTOMER_BOOKING_FAIL";

export function getCustomerOrder() {
	return function (dispatch, getState) {

		// Get the current token and user.
		const { session: { token, user } } = getState();
		// If there isn't a user, this'll be a blank order.
		if (!user) return dispatch({
			type: GET_CUSTOMER_ORDER_SUCCESS,
			order: {
				user: null,
				bookings: []
			}
		});

		dispatch({
			type: GET_CUSTOMER_ORDER_START
		});

		axios.get(`${API_ROOT}/bookings/current`, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(bookings => {
			dispatch({
				type: GET_CUSTOMER_ORDER_SUCCESS,
				order: { bookings }
			});
		}).catch(err => {
			console.error("Error getting customer order: ", err);
			dispatch({
				type: GET_CUSTOMER_ORDER_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}));
		});
	}
}

export function addBooking(rawBooking, callback) {
	return function (dispatch, getState) {
		// Remove the ID from any booking before creating, because
		// the booking data may have come from temporary state and have
		// an auto-generated ID.
		const { id, ...booking } = rawBooking;
		// Get the current token and user.
		const {
			session: { token, user },
			customerBookings: { items }
		} = getState();
		// If there isn't a user, this will be a blank order.
		if (!user) {
			// Check how many bookings already added for that day and slot
			const matches = items.filter(b => (
				b.slot.from === rawBooking.slot.from &&
				moment(b.date).isSame(moment(rawBooking.date), "day")
			));
			// And if it breaches the rule, reject.
			if (matches.length >= MAX_CONCURRENT_BOOKINGS) {
				let text = `You can only have ${MAX_CONCURRENT_BOOKINGS} bay${MAX_CONCURRENT_BOOKINGS === 1 ? "" : "s"} booked at the same time. If you need more, please call us for a group booking.`
				if (callback) callback({ message: text });
				return dispatch(addLog({
					type: "error",
					text
				}));
			}
			dispatch({
				type: ADD_CUSTOMER_BOOKING_SUCCESS,
				booking: {
					...booking,
					// Create an ID for the local booking to use when it's in state.
					id: tempId()
				}
			});
			if (callback) callback();
			return;

		}
		// Otherwise we can start the process for adding one to the database.
		dispatch({
			type: ADD_CUSTOMER_BOOKING_START
		});

		axios.post(`${API_ROOT}/bookings/current`, booking, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(booking => {
			dispatch({
				type: ADD_CUSTOMER_BOOKING_SUCCESS,
				booking
			});
			if (callback) callback();
		}).catch(err => {
			console.error("Error adding customer booking: ", err);
			dispatch({
				type: ADD_CUSTOMER_BOOKING_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}));
			if (callback) callback(err);
		});
	}
}

export function removeBooking(booking) {
	return function (dispatch, getState) {
		// Check if already loading
		const { customerBookings: { loading } } = getState();
		if (loading) return;
		// Get the current token and user.
		const { session: { token, user } } = getState();
		// If there isn't a user, this'll be a blank order.
		if (!user) return dispatch({
			type: REMOVE_CUSTOMER_BOOKING_SUCCESS,
			booking
		});

		dispatch({
			type: REMOVE_CUSTOMER_BOOKING_START
		});

		axios.delete(`${API_ROOT}/bookings/current/${booking.id}`, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(() => {
			dispatch({
				type: REMOVE_CUSTOMER_BOOKING_SUCCESS,
				booking
			});
		}).catch(err => {
			console.error("Error removing customer booking: ", err);
			dispatch({
				type: REMOVE_CUSTOMER_BOOKING_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}));
		});
	}
}

export function updateBooking(booking, changes) {
	return function (dispatch, getState) {
		// Get the current token and user.
		const { session: { token, user } } = getState();
		// If there isn't a user, this'll be a blank order.
		if (!user) {
			// Checking if we are changing number of people
			const peopleChanges = changes.adults !== undefined || changes.children !== undefined;
			// Combine the changes for some more validation.
			const proposedChangedBooking = {
				...booking,
				...changes
			}
			// Can't have no-one playing.
			if (
				peopleChanges &&
				proposedChangedBooking.adults === 0 &&
				proposedChangedBooking.children === 0
			) return dispatch(addLog({
				type: "error",
				text: "You cannot have no one playing."
			}));
			// Check for no unsupervised kids past 1800.
			if (
				peopleChanges &&
				proposedChangedBooking.children &&
				!proposedChangedBooking.adults &&
				moment(proposedChangedBooking.date).hour() >= 18
			) return dispatch(addLog({
				type: "error",
				text: "You cannot have unsupervised children after 1800."
			}));
			// Check no more than two people per booking.
			if (peopleChanges && (proposedChangedBooking.children || 0) + (proposedChangedBooking.adults || 0) > MAX_PEOPLE_PER_SESSION)
				return dispatch(addLog({
					type: "error",
					text: "Because of social distancing rules, we can't allow more than two people to a booking."
				}));
			// Make the update
			return dispatch({
				type: UPDATE_CUSTOMER_BOOKING_SUCCESS,
				booking: proposedChangedBooking
			});
		}

		dispatch({
			type: UPDATE_CUSTOMER_BOOKING_START
		});

		axios.put(`${API_ROOT}/bookings/current/${booking.id}`, changes, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(booking => {
			dispatch({
				type: UPDATE_CUSTOMER_BOOKING_SUCCESS,
				booking
			});
		}).catch(err => {
			console.error("Error adding customer booking: ", err);
			dispatch({
				type: UPDATE_CUSTOMER_BOOKING_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}));
		});
	}
}

export function checkout(nonce, withLoyaltyCard, cb) {
	return function (dispatch, getState) {
		// Get the current token and user.
		const { session: { token } } = getState();

		dispatch({
			type: CHECKOUT_START
		});
		// Make the checkout request, posting the Braintree nonce.
		axios.post(`${API_ROOT}/orders/braintree-checkout`, {
			nonce,
			withLoyaltyCard
		}, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(() => {
			if (cb) cb();
			dispatch({
				type: CHECKOUT_SUCCESS
			});
			// Displaying success message
			dispatch(addLog({
				type: "success",
				text: "Order created"
			}));
		}).catch(err => {
			console.error("Error checking out: ", err);
			dispatch({
				type: CHECKOUT_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}));
		});
	}
}

export function loyaltyCheckout(withLoyaltyCard, cb) {
	return function (dispatch, getState) {
		// Get the current token and user.
		const { session: { token } } = getState();

		dispatch({
			type: LOYALTY_CHECKOUT_START
		});
		// Make the checkout request
		axios.post(`${API_ROOT}/orders/loyalty-checkout`, { withLoyaltyCard }, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(() => {
			if (cb) cb();
			dispatch({
				type: LOYALTY_CHECKOUT_SUCCESS
			});
			// Displaying success message
			dispatch(addLog({
				type: "success",
				text: "Order created"
			}));
		}).catch(err => {
			console.error("Error checking out: ", err);
			dispatch({
				type: LOGIN_REQUEST_FAIL
			});
			dispatch(addLog({
				type: "error",
				text: errorResponse(err)
			}))
		})
	}
}

export function ping() {
	return function (dispatch, getState) {
		// Get the current token and user.
		const { session: { token, user } } = getState();

		// If no user return a error log
		if (!user) {
			return dispatch(addLog({
				type: "error",
				text: "You must be logged in to ping bookings"
			}))
		};

		dispatch({
			type: PING_CUSTOMER_BOOKING_START
		});

		// Make the ping request
		axios.get(`${API_ROOT}/bookings/ping`, {
			headers: token ? {
				Authorization: `Bearer ${token}`
			} : {}
		}).then(res => res.data).then(bookings => {
			dispatch({
				type: PING_CUSTOMER_BOOKING_SUCCESS,
				bookings
			});
			// dispatch(addLog({
			//     type: "success",
			//     text: "PING PING PING"
			// }));
		}).catch(err => {
			console.error(errorResponse(err));
			dispatch({
				type: PING_CUSTOMER_BOOKING_FAIL
			})
		})
	}
}